import { useState } from 'react';
import { Icon } from '../../../atoms/Icon/Icon';
import { Modal } from '../../../molecules/Modal/Modal';
import { Select, type SelectOption } from '../../../molecules/Select/Select';
import styles from './ExportModal.module.css';

/**
 * `Export report` — Figma `Custom reports - Create new report(export)`
 * (2069:33330), and since 2026-09-21 **the** export dialog: every `Export`
 * button in the app opens this, per the user.
 *
 * `Modal` at `Type=Dropdown`: header, a slot holding one `Select` labelled
 * `Choose format`, and the fill actions. Extracted from `ExportReportModal`
 * when it stopped belonging to Custom reports alone — that one is now a thin
 * wrapper that feeds this from the reports store.
 *
 * **`Confirm` closes the dialog and produces nothing** (settled with the user
 * 2026-09-20). Rendering a page to PDF or PPT is a server's job, and neither
 * can be done in the browser without a dependency this repo does not carry. A
 * CSV could in principle be built client-side, but it would need every widget
 * on the page to hand over its rows, which is a data contract nothing in the
 * prototype has — so it produces nothing either, like the other two. The
 * dialog is the flow, wired and measurable; the file it would produce is not
 * part of it. That is why each caller's `description` says what an export
 * *would cover* rather than promising a download.
 *
 * The title stays Figma's `Export report` everywhere. Only one export frame is
 * drawn, and what it exports from a module page or a result set is still a
 * report of it; the description is what varies, and each caller writes its own.
 */

/**
 * Figma draws `PDF` as the value; `PPT` (2026-09-20) and `CSV` (2026-09-26) are
 * the user's additions. `CSV` last because it is the odd one of the three — the
 * other two export the page as it is drawn, this one exports the numbers under
 * it — and because a reader scanning for a document format should meet the two
 * document formats first.
 */
const FORMATS: SelectOption[] = [
  { value: 'pdf', label: 'PDF' },
  { value: 'ppt', label: 'PPT' },
  { value: 'csv', label: 'CSV' },
];

export type ExportModalProps = {
  open: boolean;
  onClose: () => void;
  /** Figma `Description` on `_Modal header` — what this export would cover. */
  description: string;
};

export function ExportModal({ open, onClose, description }: ExportModalProps) {
  const [format, setFormat] = useState('pdf');
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    /* Back to the frame's default each time, rather than remembering a choice
       nothing was ever done with. */
    if (open) setFormat('pdf');
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      icon={<Icon name="export" />}
      title="Export report"
      description={description}
      cancelLabel="Cancel"
      confirmLabel="Confirm"
      onConfirm={onClose}
    >
      <div className={styles.formatField}>
        {/* Figma `Select` — `Type=Default`, label on, required off, hint off. */}
        <Select label="Choose format" options={FORMATS} value={format} onValueChange={setFormat} />
      </div>
    </Modal>
  );
}
